import { useCallback, useContext, useMemo, useState } from "react";

import { parse } from "date-fns";
import { useMatch, useNavigate, useParams } from "react-router";

import { DialogContext } from "@/components/layout/DialogProvider";
import {
  CertificationDetailsDocument,
  CertificationsDocument,
  ExpirationInterval,
  ExpirationType,
  useCertificationDetailsQuery,
  useDeleteCertificationMutation,
  useIssueManuallyMutation,
  useRevokeCertificateMutation,
} from "@/graphql";

export function useCertificationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data } = useCertificationDetailsQuery({
    variables: { id: id! },
  });
  const [error, setError] = useState<string | null>(null);
  const isEditOpen = useMatch("/certifications/:id/edit");
  const handleEdit = useCallback(() => {
    navigate(`/certifications/${id}/edit`);
  }, []);
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      navigate(`/certifications/${id}`);
    }
  }, []);

  const [issueManually] = useIssueManuallyMutation();
  const [revoke] = useRevokeCertificateMutation();
  const { showConfirmationDialog } = useContext(DialogContext);

  const handleIssueManually = useCallback(
    (
      userIds: string[],
      sendNotifications: boolean,
      onClose: (error?: string) => void,
      note?: string,
    ) => {
      issueManually({
        variables: { certificationId: id!, userIds, note, sendNotifications },
        onCompleted: ({ issueCertificates }) => {
          issueCertificates?.ok
            ? onClose()
            : onClose(
                issueCertificates?.errors
                  .map((error) => error.messages?.join(", "))
                  .join(", "),
              );
        },
        onError: (error) => {
          onClose(error.message);
        },
        refetchQueries: [
          { query: CertificationDetailsDocument, variables: { id } },
          "CertificationParticipants",
        ],
      });
    },

    [],
  );

  const handleRevoke = useCallback((certificateId: string) => {
    setError(null);
    showConfirmationDialog({
      title: "Revoke Certificate",
      description: "Are you sure you want to revoke this certificate?",
      intent: "danger",
      onConfirm: () => {
        revoke({
          variables: { certificateId },
          onCompleted: ({ revokeCertificate }) => {
            if (!revokeCertificate?.ok) {
              setError(
                revokeCertificate?.errors
                  ?.map((error) => error?.messages?.join(", "))
                  .join(", ") ?? "",
              );
            }
          },
          onError: (error) => {
            setError(error.message);
          },
          refetchQueries: [
            { query: CertificationDetailsDocument, variables: { id } },
            "CertificationParticipants",
          ],
        });
      },
      onCancel: () => {},
    });
  }, []);

  const [deleteCertification] = useDeleteCertificationMutation();
  const handleDelete = useCallback(() => {
    setError(null);
    showConfirmationDialog({
      title: "Delete Certification",
      description: "Are you sure you want to delete this certification?",
      intent: "danger",
      onConfirm: () => {
        deleteCertification({
          variables: { id: id! },
          onCompleted: ({ deleteCertification }) => {
            if (!deleteCertification?.ok) {
              setError(
                deleteCertification?.errors
                  ?.map((error) => error?.messages?.join(", "))
                  .join(", ") ?? "",
              );
            } else {
              navigate("/certifications");
            }
          },
          onError: (error) => {
            setError(error.message);
          },
          refetchQueries: [CertificationsDocument],
        });
      },
      onCancel: () => {},
    });
  }, []);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const availableGroups = useMemo(() => {
    return (
      data?.currentWorkspace?.groups?.map((group) => ({
        id: group.id,
        name: group.name,
      })) ?? []
    );
  }, [data?.currentWorkspace?.groups]);

  const initialExpiration = useMemo(() => {
    return {
      type: data?.certification?.expirationType ?? ExpirationType.NoExpiration,
      relativeExpirationInterval:
        data?.certification?.expirationInterval ?? ExpirationInterval.Day,
      relativeExpirationValue: data?.certification?.expirationValue ?? 0,
      absoluteExpiration:
        (
          data?.certification?.expirationDate &&
          parse(data.certification.expirationDate, "yyyy-MM-dd", new Date())
        )?.toString() ?? null,
    };
  }, [data?.certification]);

  return {
    data,
    error,
    isEditOpen,
    handleEdit,
    handleOpenChange,
    handleIssueManually,
    handleRevoke,
    handleDelete,
    handleCloseError,
    availableGroups,
    initialExpiration,
  };
}
